import {
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from "@mui/material";
import {
  useGetOne,
  Show,
  useGetMany,
  Tab,
  TabbedShowLayout,
  TextField,
  BooleanField,
  Datagrid,
  ReferenceManyField,
  ReferenceField,
  EmailField,
  FunctionField,
  NumberField,
  useShowController,
  useRecordContext,
  DateField,
  useNotify,
  useRefresh,
  Button,
  Confirm,
  TopToolbar,
  usePermissions,
} from "react-admin";
import { ResultShow } from "./result-show";
import { useParams } from "react-router-dom";
import { useState } from "react";
import ApiClient from "../../ApiClient";
import API from "../../Routes";
import { RestartAlt, Archive } from "@mui/icons-material";
import { isInternalAdmin } from "../../auth-provider";

const STAGE = process.env.REACT_APP_STAGE || "staging";
const EMPLOYEE_ENDPOINT =
  STAGE === "production"
    ? "https://app.thrivetech.co/employees"
    : "https://app.chasana.biz/employees";

// Components used in ProjectTeamsShow
const TeamShowTitle = () => {
  const record = useRecordContext();
  return <span>Team {record ? `"${record.name}"` : ""}</span>;
};

const ArchiveButton = () => {
  const [open, setOpen] = useState(false);
  const record = useRecordContext();
  const refresh = useRefresh();
  const notify = useNotify();
  const isArchived = record?.archived;

  return (
    <>
      <Button
        label={isArchived ? "Unarchive team" : "Archive team"}
        onClick={() => setOpen(true)}
        color="error"
      >
        {isArchived ? <RestartAlt /> : <Archive />}
      </Button>
      <Confirm
        isOpen={open}
        title={isArchived ? "Unarchive team" : "Archive team"}
        content={`Are you sure you want to ${
          isArchived ? "restore" : "archive"
        } team ${record?.name} (${record?.id})?`}
        onConfirm={async () => {
          setOpen(false);
          try {
            await ApiClient.fetch(
              API.TEAMS[isArchived ? "RESTORE" : "ARCHIVE"](record?.id),
              {
                method: "POST",
              }
            );
            notify(
              `Team ${record?.id} ${isArchived ? "restored" : "archived"}`
            );
            refresh();
          } catch (error) {
            notify(
              `Failed to ${isArchived ? "restore" : "archive"} team ${
                record?.id
              }: ${error?.message || error}`,
              { type: "error" }
            );
          }
        }}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const TeamShowActions = (props) => {
  return (
    <TopToolbar {...props}>
      <ArchiveButton />
    </TopToolbar>
  );
};

const MostCommonInsightAnswers = () => {
  const record = useRecordContext();
  return (
    <Box p={2} backgroundColor="#31313130" borderRadius={2}>
      <Typography variant="body1" mb={1}>
        Most Common Answers
      </Typography>
      <Datagrid
        data={record.mostCommonAnswersByType}
        rowClick={false}
        bulkActionButtons={false}
      >
        <TextField source="selectedChoice" />
        <NumberField source="amountOfCompletedAnswers" />
        <NumberField source="totalAmountOfAnswers" />
      </Datagrid>
    </Box>
  );
};

// Project Teams Show component
export const ProjectTeamsShow = (props) => {
  const { id } = useParams();
  const record = useShowController(props).record;
  const projectId = record?.projectId;
  const permissions = usePermissions();

  const { data, isLoading, error } = useGetOne("projectTeamsResults", {
    id: id,
  });

  // Competencies fetch
  const competencyIds = data?.team?.competencies
    ? data.team.competencies.map((c) => c.id)
    : [];
  const { data: competencies } = useGetMany("competencies", {
    ids: competencyIds,
  });

  // Surveys fetch
  const surveys = data?.team?.surveys || [];
  const { data: surveysResults } = useGetOne(
    "teams",
    {
      meta: {
        endpoint: `/teams/${id}/surveys`,
        id: id,
      },
    },
    {
      enabled: !!id && surveys.length > 0,
    }
  );

  const { data: competenciesResults } = useGetOne(
    "teams",
    {
      meta: {
        endpoint: `/teams/${id}/competencies`,
        id: id,
      },
    },
    {
      enabled: !!id && competencies?.length > 0,
    }
  );

  // Insights fetch
  const insights = data?.team?.insights || [];
  const { data: insightsResults } = useGetOne(
    "teams",
    {
      meta: {
        endpoint: `/teams/${id}/overall/insights`,
        id: id,
      },
    },
    {
      enabled: !!id && insights?.length > 0,
    }
  );

  if (isLoading) return <span>Loading team results data...</span>;
  if (error) return <span>Error loading team data</span>;

  return (
    <Show
      {...props}
      title={<TeamShowTitle />}
      record={data}
      actions={
        isInternalAdmin(permissions.permissions) ? <TeamShowActions /> : null
      }
    >
      <TabbedShowLayout>
        <Tab label="Summary">
          <TextField source="id" />
          <TextField source="name" />
          <DateField record={data.team} source="created" showTime={false} />
          <TextField source="jobFunction" />
          <TextField record={data.team} source="amountOfEmployees" />
          <ReferenceField
            source="projectId"
            reference="projects"
            label="Project"
            link={(record, _reference) => `/projects/${record.id}/show`}
          >
            <TextField source="name" />
          </ReferenceField>
          <BooleanField source="archived" />
          <Divider sx={{ my: 2 }} />
          <Box>
            <Box mb={2}>
              <Typography variant="h6" color="textSecondary" fontWeight="bold">
                Competencies
              </Typography>
              {competencies?.length > 0 ? (
                <>
                  <Stack direction="row" spacing={1} mt={1}>
                    <>
                      {competencies.map((competency) => (
                        <Chip key={competency.id} label={competency.name} />
                      ))}
                    </>
                  </Stack>
                  <Stack direction="row" spacing={1} mb={1} mt={1}>
                    <Typography variant="body2" color="textSecondary">
                      Frequency:
                    </Typography>
                    <Typography variant="body2" color="textPrimary">
                      {data.team.competencies[0].frequency}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} mb={1} mt={1}>
                    <Typography variant="body2" color="textSecondary">
                      Current run start date:
                    </Typography>
                    <DateField
                      record={data.team.competencies[0]}
                      source="currentRunStartDate"
                      showTime={false}
                      emptyText="-"
                      variant="body2"
                    />
                  </Stack>
                  <Stack direction="row" spacing={1} mb={1} mt={1}>
                    <Typography variant="body2" color="textSecondary">
                      Next run start date:
                    </Typography>
                    <DateField
                      record={data.team.competencies[0]}
                      source="nextRunStartDate"
                      showTime={false}
                      emptyText="-"
                      variant="body2"
                    />
                  </Stack>
                </>
              ) : (
                <Stack>
                  <Typography variant="body2" color="textSecondary">
                    No competencies
                  </Typography>
                </Stack>
              )}
            </Box>
            <Box mt={2}>
              <Typography variant="h6" color="textSecondary" fontWeight="bold">
                Surveys
              </Typography>
              {surveys.length > 0 ? (
                <Table size="small" sx={{ mt: 1, mb: 2 }}>
                  <TableHead>
                    <TableRow sx={{ borderBottom: "2px solid white" }}>
                      <TableCell
                        align="left"
                        sx={{ fontWeight: "bold", color: "text.secondary" }}
                      >
                        TYPE
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ fontWeight: "bold", color: "text.secondary" }}
                      >
                        FREQUENCY
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ fontWeight: "bold", color: "text.secondary" }}
                      >
                        CURRENT RUN START DATE
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ fontWeight: "bold", color: "text.secondary" }}
                      >
                        NEXT RUN START DATE
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {surveys.map((survey, index) => (
                      <TableRow key={index}>
                        <TableCell align="left">{survey.type}</TableCell>
                        <TableCell align="left">
                          {survey.surveyFrequency}
                        </TableCell>
                        <TableCell align="left">
                          <DateField
                            record={survey}
                            source="currentRunStartDate"
                            showTime={false}
                            emptyText="-"
                          />
                        </TableCell>
                        <TableCell align="left">
                          <DateField
                            record={survey}
                            source="nextRunStartDate"
                            showTime={false}
                            emptyText="-"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Stack>
                  <Typography variant="body2" color="textSecondary">
                    No surveys
                  </Typography>
                </Stack>
              )}
            </Box>
            <Box mt={2}>
              <Typography variant="h6" color="textSecondary" fontWeight="bold">
                Insights
              </Typography>
              {insights.length > 0 ? (
                <>
                  <Stack direction="row" spacing={1} mt={1}>
                    {insights.map((insight) => (
                      <Chip key={insight.id} label={insight.name} />
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={1} mb={1} mt={1}>
                    <Typography variant="body2" color="textSecondary">
                      Frequency:
                    </Typography>
                    <Typography variant="body2" color="textPrimary">
                      {insights[0].frequency}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} mb={1} mt={1}>
                    <Typography variant="body2" color="textSecondary">
                      Current run start date:
                    </Typography>
                    <DateField
                      record={insights[0]}
                      source="currentRunStartDate"
                      showTime={false}
                      emptyText="-"
                      variant="body2"
                    />
                  </Stack>
                  <Stack direction="row" spacing={1} mb={1} mt={1}>
                    <Typography variant="body2" color="textSecondary">
                      Next run start date:
                    </Typography>
                    <DateField
                      record={insights[0]}
                      source="nextRunStartDate"
                      showTime={false}
                      emptyText="-"
                      variant="body2"
                    />
                  </Stack>
                </>
              ) : (
                <Stack>
                  <Typography variant="body2" color="textSecondary">
                    No Insights
                  </Typography>
                </Stack>
              )}
            </Box>
          </Box>
        </Tab>
        <Tab label="Results">
          <Stack direction="column" spacing={3}>
            {/* Overall results section: competencies and surveys */}
            {data?.overallResults?.length ? (
              <Stack direction="column" spacing={1}>
                <Typography
                  variant="h6"
                  color="textSecondary"
                  fontWeight="bold"
                >
                  Overall results
                </Typography>
                <Datagrid
                  data={data.overallResults}
                  rowClick={false}
                  bulkActionButtons={false}
                >
                  <TextField source="type" label="Type" />
                  <TextField source="score" label="Score" />
                  <TextField source="fit" label="Fit" />
                  <TextField
                    source="amountOfCompletedEmployees"
                    label="Completed Employees"
                    emptyText="NA"
                  />
                </Datagrid>
              </Stack>
            ) : null}
            {/* Competencies results breakdown section */}
            {competenciesResults?.overallCompetencyResults && (
              <Stack direction="column" spacing={1}>
                <Typography
                  variant="h6"
                  color="textSecondary"
                  fontWeight="bold"
                >
                  Behaviour results breakdown
                </Typography>
                <Datagrid
                  data={competenciesResults.overallCompetencyResults}
                  rowClick={false}
                  bulkActionButtons={false}
                >
                  <TextField source="name" label="Competency" />
                  <TextField source="score" label="Score" />
                  <TextField source="fit" label="Fit" />
                </Datagrid>
              </Stack>
            )}
            {/* Surveys results breakdown section */}
            {surveysResults?.overallSurveyResults && (
              <Stack direction="column" spacing={1}>
                <Typography
                  variant="h6"
                  color="textSecondary"
                  fontWeight="bold"
                >
                  Surveys results breakdown
                </Typography>
                <Datagrid
                  data={surveysResults.overallSurveyResults}
                  rowClick={false}
                  bulkActionButtons={false}
                >
                  <TextField source="type" label="Survey" />
                  <TextField source="score" label="Score" />
                  <TextField source="overallFit" label="Fit" />
                  <TextField
                    source="amountOfEmployees"
                    label="Total Employees"
                    emptyText="NA"
                  />
                  <TextField
                    source="amountOfCompletedEmployees"
                    label="Completed Employees"
                    emptyText="NA"
                  />
                </Datagrid>
              </Stack>
            )}
            {/* Insights results breakdown section */}
            {insightsResults?.overallInsightResults && (
              <Stack direction="column" spacing={1}>
                <Typography
                  variant="h6"
                  color="textSecondary"
                  fontWeight="bold"
                >
                  Insights results breakdown
                </Typography>
                <Datagrid
                  data={insightsResults.overallInsightResults}
                  rowClick={false}
                  bulkActionButtons={false}
                  expand={<MostCommonInsightAnswers />}
                  isRowExpandable={(record) =>
                    record?.mostCommonAnswersByType?.length > 0
                  }
                  sx={{ mt: 2 }}
                >
                  <TextField source="insightName" label="Insight" />
                  <TextField source="score" label="Score" />
                  <TextField source="overallFit" label="Fit" />
                </Datagrid>
              </Stack>
            )}
          </Stack>
        </Tab>
        <Tab label="Employees - results">
          <ReferenceManyField
            label={false}
            reference="results"
            target="teamId"
            id={id}
            filter={{ includeDeleted: true, projectId: projectId }}
            perPage={200}
          >
            <Datagrid
              bulkActionButtons={false}
              // expand={
              //   competencyIds?.length || surveys?.length ? (
              //     <ResultShow />
              //   ) : undefined
              // }
              expand={<ResultShow />}
            >
              <TextField source="employeeId" label="Employee Id" />
              <ReferenceField
                source="employeeId"
                reference="employees"
                label="Email"
                link={false}
              >
                <EmailField source="email" />
              </ReferenceField>
              <ReferenceField
                source="employeeId"
                reference="employees"
                label="External Correlation Id"
                link={false}
              >
                <TextField source="externalCorrelationId" />
              </ReferenceField>
              <ReferenceField
                source="employeeId"
                reference="employees"
                label="Name"
                link={false}
              >
                <FunctionField
                  render={(record) => `${record.name} ${record.lastName}`}
                />
              </ReferenceField>
              <ReferenceField
                source="employeeId"
                reference="employees"
                label="Deleted"
                link={false}
              >
                <BooleanField source="deleted" />
              </ReferenceField>
              <NumberField source="overallCompetencyScore" />
              <ReferenceField
                source="employeeId"
                reference="employees"
                label="Employee Link"
                link={false}
              >
                <FunctionField
                  render={(record) => (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`${EMPLOYEE_ENDPOINT}/${record.id}?token=${record.token}`}
                    >
                      Employee Link
                    </a>
                  )}
                />
              </ReferenceField>
            </Datagrid>
          </ReferenceManyField>
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};
